import { useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Money } from "@workspace/accounting/components";
import type { Money as MoneyValue } from "@workspace/accounting/types";
import { CheckOperation } from "@workspace/operations/check-operation";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import { Label } from "@workspace/ui/components/label";

import { eposApi } from "../../api";
import type { TabDetail, TabPaymentRecord, TabRefund } from "../../types";
import { useEposContext } from "../epos-context";
import { announceTabRefund } from "./tab-refund-outcome";

const REFUND_REASONS = [
	{ value: "customer-dissatisfied", label: "Customer Dissatisfied" },
	{ value: "wrong-order", label: "Wrong Order" },
	{ value: "quality-issue", label: "Quality Issue" },
	{ value: "overcharged", label: "Overcharged" },
	{ value: "duplicate-charge", label: "Duplicate Charge" },
	{ value: "cancelled-order", label: "Cancelled Order" },
	{ value: "other", label: "Other" },
] as const;

const methodLabel = (method: string) =>
	method === "card" ? "Card" : method === "cash" ? "Cash" : method;

const formatted = ({ value, currency }: MoneyValue) =>
	new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(
		value / 100,
	);

/** What has been refunded of a payment so far, its tip apart (a failed card refund gave nothing back). */
const refundedOf = (detail: TabDetail, tabPaymentId: string) =>
	(detail.tab.refunds ?? [])
		.filter((r) => r.tabPaymentId === tabPaymentId && r.status !== "Failed")
		.reduce(
			(sum, r) =>
				sum +
				r.line.value +
				r.orders.reduce((orders, o) => orders + o.amount.value, 0),
			0,
		);

/** A payment's refunds that are not (yet) money back: still being made, or failed (HA-983: a Failed one was hidden). */
const unsettledRefunds = (detail: TabDetail, tabPaymentId: string) =>
	(detail.tab.refunds ?? []).filter(
		(r) =>
			r.tabPaymentId === tabPaymentId &&
			(r.status === "Failed" ||
				r.status === "Processing" ||
				r.status === "Pending"),
	);

/**
 * HA-789 (ACs 14-17) — a tab's Pay Tab payments, each with what has been refunded of it and **Refund**
 * (staff-terminal-refund) while anything is left: all of it or an amount, spread over the rounds and the service charge
 * line it paid on the server.
 */
export function TabPayments({ detail }: { detail: TabDetail }) {
	const { operations } = useEposContext();
	const [refunding, setRefunding] = useState<TabPaymentRecord | null>(null);

	if (detail.tab.payments.length === 0) return null;

	return (
		<>
			<ul aria-label="Payments on this tab" className="space-y-2">
				{detail.tab.payments.map((payment) => {
					const refunded = refundedOf(detail, payment.tabPaymentId);
					const left = payment.amount.value - refunded;
					return (
						<li
							key={payment.tabPaymentId}
							className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
						>
							<span className="flex flex-col">
								<span className="font-medium">
									{methodLabel(payment.method)} <Money {...payment.amount} />
								</span>
								{payment.tip && payment.tip.value > 0 && (
									<span className="text-xs text-muted-foreground">
										tip <Money {...payment.tip} />
									</span>
								)}
								{refunded > 0 && (
									<span className="text-xs text-muted-foreground">
										refunded <Money {...payment.amount} value={refunded} />
									</span>
								)}
								{unsettledRefunds(detail, payment.tabPaymentId).map((r) => (
									<UnsettledRefund
										key={r.refundId}
										detail={detail}
										refund={r}
									/>
								))}
							</span>
							{left > 0 && (
								<CheckOperation
									operation="staff-terminal-refund"
									operations={operations}
								>
									<Button
										size="sm"
										variant="outline"
										onClick={() => setRefunding(payment)}
									>
										<RotateCcw className="size-4" />
										Refund{" "}
										<span className="sr-only">
											{`the ${payment.method} payment of `}
											<Money {...payment.amount} />
										</span>
									</Button>
								</CheckOperation>
							)}
						</li>
					);
				})}
			</ul>
			{refunding && (
				<RefundTabPaymentDialog
					detail={detail}
					payment={refunding}
					onClose={() => setRefunding(null)}
				/>
			)}
		</>
	);
}

/**
 * HA-983 — one refund of a payment that is not money back yet. Still being made: it says so. Failed: it says so with Stripe's reason, and
 * (for whoever may refund) a **Retry** that sends the SAME refund again, so Stripe cannot refund it twice.
 */
function UnsettledRefund({
	detail,
	refund,
}: {
	detail: TabDetail;
	refund: TabRefund;
}) {
	const { tenant, venueId, operations } = useEposContext();
	const queryClient = useQueryClient();

	const retry = useMutation({
		...eposApi.retryTabRefundOptions(tenant, venueId),
		onSuccess(answer) {
			queryClient.setQueryData(
				[tenant, venueId, "epos", "tabs", answer.tab.tab.id],
				answer.tab,
			);
			void queryClient.invalidateQueries({
				queryKey: [tenant, venueId, "epos"],
			});
			announceTabRefund(answer.refund);
		},
		onError(error) {
			toast.error("Couldn't retry the refund", {
				description: error instanceof Error ? error.message : "Unknown error",
			});
		},
	});

	if (refund.status !== "Failed") {
		return (
			<span className="text-xs text-muted-foreground">
				Refund of <Money {...refund.total} /> processing
			</span>
		);
	}

	return (
		<span className="flex flex-col gap-1 text-xs text-destructive">
			<span>
				Refund of <Money {...refund.total} /> failed
			</span>
			{refund.errorMessage && (
				<span className="text-muted-foreground">{refund.errorMessage}</span>
			)}
			{refund.method === "card" && (
				<CheckOperation
					operation="staff-terminal-refund"
					operations={operations}
				>
					<Button
						size="sm"
						variant="outline"
						className="w-fit"
						disabled={retry.isPending}
						onClick={() =>
							retry.mutate({ tabId: detail.tab.id, refundId: refund.refundId })
						}
					>
						{retry.isPending ? (
							<Loader2 className="size-4 animate-spin" />
						) : (
							<RotateCcw className="size-4" />
						)}
						Retry{" "}
						<span className="sr-only">
							the failed refund of <Money {...refund.total} />
						</span>
					</Button>
				</CheckOperation>
			)}
		</span>
	);
}

/**
 * The refund of one Pay Tab payment: **Refund all** (what is left of it) or **Refund an amount**; "Refund the £X tip too?"
 * only on a full refund of a tipped payment; a reason. The server spreads it and answers with each round's part, the line's
 * and the tip's, shown here.
 */
function RefundTabPaymentDialog({
	detail,
	payment,
	onClose,
}: {
	detail: TabDetail;
	payment: TabPaymentRecord;
	onClose: () => void;
}) {
	const { tenant, venueId } = useEposContext();
	const queryClient = useQueryClient();
	const [full, setFull] = useState(true);
	const [amountText, setAmountText] = useState("");
	const [refundTip, setRefundTip] = useState(false);
	const [reason, setReason] = useState("");
	const [problem, setProblem] = useState<string | null>(null);
	const [result, setResult] = useState<TabRefund | null>(null);

	const refund = useMutation({
		...eposApi.refundTabPaymentOptions(tenant, venueId),
		onSuccess(answer) {
			queryClient.setQueryData(
				[tenant, venueId, "epos", "tabs", answer.tab.tab.id],
				answer.tab,
			);
			void queryClient.invalidateQueries({
				queryKey: [tenant, venueId, "epos"],
			});
			setResult(answer.refund);
			announceTabRefund(answer.refund);
		},
		onError(error) {
			toast.error("Couldn't refund the payment", {
				description: error instanceof Error ? error.message : "Unknown error",
			});
		},
	});

	const left = payment.amount.value - refundedOf(detail, payment.tabPaymentId);
	const tip = payment.tip && payment.tip.value > 0 ? payment.tip : null;

	const submit = () => {
		if (!reason) {
			setProblem("A reason is required");
			return;
		}
		let amount: MoneyValue | null = null;
		if (!full) {
			const value = Math.round(Number.parseFloat(amountText) * 100);
			if (!Number.isFinite(value) || value <= 0 || value > left) {
				setProblem(
					`Enter an amount up to ${formatted({ ...payment.amount, value: left })}`,
				);
				return;
			}
			amount = { ...payment.amount, value };
		}
		setProblem(null);
		refund.mutate({
			tabId: detail.tab.id,
			tabPaymentId: payment.tabPaymentId,
			amount,
			refundTip: full && !!tip && refundTip,
			reason,
		});
	};

	const roundOf = (orderId: string) =>
		detail.orders.findIndex((o) => o.id === orderId) + 1;
	const tipBack =
		result?.orders.reduce((sum, o) => sum + (o.tip?.value ?? 0), 0) ?? 0;

	return (
		<Dialog open onOpenChange={(open) => !open && onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Refund the payment</DialogTitle>
					<DialogDescription>
						{methodLabel(payment.method)} <Money {...payment.amount} /> ·{" "}
						<Money {...payment.amount} value={left} /> left to refund
					</DialogDescription>
				</DialogHeader>

				{result ? (
					<ul aria-label="Refunded" className="space-y-1 text-sm">
						{result.orders
							.filter((o) => o.amount.value > 0)
							.map((o) => (
								<li key={o.orderRefundId} className="flex justify-between">
									Round {roundOf(o.orderId)} <Money {...o.amount} />
								</li>
							))}
						{result.line.value > 0 && (
							<li className="flex justify-between">
								Service charge <Money {...result.line} />
							</li>
						)}
						{tipBack > 0 && (
							<li className="flex justify-between">
								Tip <Money {...result.total} value={tipBack} />
							</li>
						)}
					</ul>
				) : (
					<form
						id="refund-tab-payment"
						className="space-y-3"
						onSubmit={(event) => {
							event.preventDefault();
							submit();
						}}
					>
						<div className="flex gap-2">
							<Button
								type="button"
								variant={full ? "default" : "outline"}
								aria-pressed={full}
								onClick={() => setFull(true)}
							>
								Refund all
							</Button>
							<Button
								type="button"
								variant={full ? "outline" : "default"}
								aria-pressed={!full}
								onClick={() => setFull(false)}
							>
								Refund an amount
							</Button>
						</div>
						{!full && (
							<div className="space-y-1">
								<Label htmlFor="refund-tab-payment-amount">
									Amount to refund (£)
								</Label>
								<Input
									id="refund-tab-payment-amount"
									inputMode="decimal"
									value={amountText}
									onChange={(event) => setAmountText(event.target.value)}
								/>
							</div>
						)}
						{full && tip && (
							<label className="flex items-center gap-2 text-sm">
								<input
									type="checkbox"
									checked={refundTip}
									onChange={(event) => setRefundTip(event.target.checked)}
								/>
								{`Refund the ${formatted(tip)} tip too?`}
							</label>
						)}
						<div className="space-y-1">
							<Label htmlFor="refund-tab-payment-reason">Reason</Label>
							<select
								id="refund-tab-payment-reason"
								className="h-10 w-full rounded-md border bg-background px-3 text-sm"
								value={reason}
								onChange={(event) => setReason(event.target.value)}
							>
								<option value="">Choose a reason</option>
								{REFUND_REASONS.map((r) => (
									<option key={r.value} value={r.value}>
										{r.label}
									</option>
								))}
							</select>
						</div>
						{problem && (
							<p role="alert" className="text-sm text-destructive">
								{problem}
							</p>
						)}
					</form>
				)}

				<DialogFooter>
					{result ? (
						<Button onClick={onClose}>Done</Button>
					) : (
						<Button
							type="submit"
							form="refund-tab-payment"
							disabled={refund.isPending}
						>
							{refund.isPending && <Loader2 className="size-4 animate-spin" />}
							Refund
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
